import {
  WSElement,
  WSFlexBox,
  WSGrid,
  WSPill,
  WSText
} from "@wingspanhq/fe-component-library";
import { useMemo } from "react";
import { mapRequirementDefinitionTemplateTierConfig } from "../../../config/maps";
import {
  RequirementDefinitionTemplate,
  RequirementDefinitionTemplateTier
} from "../types";
import { Item } from "./Item";
import { useFeatureFlags } from "../../../../../query/hooks/useFeatureFlags";
import { ExtendedRequirementDefinition } from "../../../../../query/requirementDefinitions/query/useQueryExtendedRequirementDefinition";
import { getRequirementDefinitionTemplates } from "../../../utils/getRequirementDefinitionTemplates";
import { selectorIsPolicyTypesMatched } from "../../../../Requirements/modules/InsuranceCoverage/selectors/selectorIsPolicyTypesMatched";
import type { insuranceMonitorApiSpec } from "@wingspanhq/wingspan-api-types";
import { selectorIsContextTypesMatched } from "../../../../Requirements/modules/InsuranceCoverage/selectors/selectorIsContextTypesMatched";

type InsuranceCoverageMonitorSettings =
  insuranceMonitorApiSpec.InsuranceCoverageMonitorSettings;

type Props = {
  tier: RequirementDefinitionTemplateTier;
  onSelect: (item: RequirementDefinitionTemplate) => void;
  selected: RequirementDefinitionTemplate | null;
  requirements: ExtendedRequirementDefinition[];
};

export const Section: React.FC<Props> = props => {
  const queryFeatureFlags = useFeatureFlags();
  const existingRequirements = props.requirements;

  const isWayfinder =
    mapRequirementDefinitionTemplateTierConfig[props.tier].isWayfinder;

  const isSmartAdvancedEnabled = queryFeatureFlags.data?.wayfinderSmartAdvanced;

  const filteredItems = useMemo(
    () =>
      getRequirementDefinitionTemplates().filter(
        item =>
          item.tier === props.tier &&
          (item.isSmartAdvanced ? isSmartAdvancedEnabled : true)
      ),
    [props.tier, isSmartAdvancedEnabled]
  );

  return (
    <WSElement>
      <WSFlexBox alignItems="center" gap="M" mb="L">
        <WSText.Caption weight="medium">
          {mapRequirementDefinitionTemplateTierConfig[props.tier].name}
        </WSText.Caption>

        {isWayfinder ? (
          <WSPill theme="violet" outline text="Wayfinder" icon="compass" />
        ) : null}
      </WSFlexBox>

      <WSGrid>
        {filteredItems.map(item => {
          const existingRequirement = existingRequirements.find(r => {
            const isRequirementTypeMatched =
              item.requirementType === r.requirementType;

            const isContextTypeMatched = item.contextType
              ? selectorIsContextTypesMatched(r.dataSource, item.contextType)
              : true;

            const isPolicyTypeMatched = item.policyType
              ? selectorIsPolicyTypesMatched(
                  r.dataSource as InsuranceCoverageMonitorSettings,
                  item.policyType
                )
              : true;

            return (
              isRequirementTypeMatched &&
              isContextTypeMatched &&
              isPolicyTypeMatched
            );
          });

          return (
            <WSGrid.Item key={item.key} span={{ s: "6", m: "4" }}>
              <Item
                itemConfig={item}
                tierConfig={
                  mapRequirementDefinitionTemplateTierConfig[props.tier]
                }
                onSelect={props.onSelect}
                selected={props.selected}
                existingRequirementId={
                  item.singleInstance
                    ? existingRequirement?.requirementDefinitionId
                    : undefined
                }
              />
            </WSGrid.Item>
          );
        })}
      </WSGrid>
    </WSElement>
  );
};
